import React, { useState, useMemo } from 'react'
import { Button, Form, Modal, Pagination, Table } from 'react-bootstrap'

// ─── helpers ─────────────────────────────────────────────────────────────────

const isAdmin = () => localStorage.admin === 'SIM'

const getColorClass = (d) => {
  const l = d?.charAt(0).toUpperCase()
  if (l === 'S') return 'Saida'
  if (l === 'E') return 'Entrada'
  if (l === 'B') return 'Balanço'
  if (l === 'C') return 'CallBack'
  return ''
}

const getColor = (d) => {
  const l = d?.charAt(0).toUpperCase()
  if (l === 'S') return 'text-danger'
  if (l === 'E') return 'text-success'
  if (l === 'B') return 'text-info'
  if (l === 'C') return 'text-success'
  return ''
}

// Aceita qualquer casing de campo (UPPER, lower, misto)
const campo = (obj, key) => {
  if (!obj) return null
  if (obj[key]               !== undefined) return obj[key]
  if (obj[key.toUpperCase()] !== undefined) return obj[key.toUpperCase()]
  if (obj[key.toLowerCase()] !== undefined) return obj[key.toLowerCase()]
  return null
}

const brl = (v) =>
  v != null && !isNaN(Number(v))
    ? Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '—'

const num = (v, casas = 2) =>
  v != null && !isNaN(Number(v)) ? Number(v).toFixed(casas) : null

const dataMs = (d) => {
  if (!d) return 0
  const t = new Date(d).getTime()
  return isNaN(t) ? 0 : t
}

const PctSpan = ({ v, positiveGreen = true }) => {
  if (v == null || isNaN(Number(v))) return <span style={{ color: '#64748b' }}>—</span>
  const n = parseFloat(Number(v).toFixed(1))
  const color = n > 0
    ? (positiveGreen ? '#4ade80' : '#f87171')
    : n < 0
      ? (positiveGreen ? '#f87171' : '#4ade80')
      : '#94a3b8'
  return <span style={{ color, fontWeight: 700 }}>{n > 0 ? '+' : ''}{n}%</span>
}

// ─── ícone ────────────────────────────────────────────────────────────────────

const IconIA = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
)

// ─── constantes ───────────────────────────────────────────────────────────────

const RISCO_COR = { alto: '#ef4444', medio: '#f59e0b', baixo: '#22c55e' }
const TEND_ICON = { crescimento: '↑', queda: '↓', estavel: '→', subindo: '↑', caindo: '↓', 'sem dados': '—' }

// ─── sub-componentes internos ─────────────────────────────────────────────────

const Metric = ({ valor, label, color, small }) => (
  <div style={S.metric}>
    <span style={{ ...S.metricVal, fontSize: small ? 14 : 20, ...(color ? { color } : {}) }}>
      {valor ?? '—'}
    </span>
    <span style={S.metricLbl}>{label}</span>
  </div>
)

const Row = ({ label, children }) => (
  <div style={S.kv}>
    <span style={S.kvLabel}>{label}</span>
    <span style={S.kvValue}>{children}</span>
  </div>
)

const Badge = ({ label, color = '#334155' }) => (
  <span style={{ ...S.badge, background: color }}>
    {TEND_ICON[label] ? `${TEND_ICON[label]} ` : ''}{label}
  </span>
)

const Section = ({ icon, title, right, children }) => (
  <div style={S.section}>
    <div style={S.sectionHead}>
      <span>{icon}</span>
      <span style={S.sectionTitle}>{title}</span>
      {right && <span style={{ marginLeft: 'auto' }}>{right}</span>}
    </div>
    {children}
  </div>
)

// ─── tabela log alterações ────────────────────────────────────────────────────

const TabelaLog = ({ logAlteracoes }) => {
  const rows = useMemo(() => logAlteracoes
    .map(l => ({
      data:     campo(l, 'DATA_ALTERACAO'),
      custoOld: parseFloat(campo(l, 'PRECOCUSTO_OLD')),
      custoNew: parseFloat(campo(l, 'PRECOCUSTO_NEW')),
      vendaOld: parseFloat(campo(l, 'PRECOVENDA_OLD')),
      vendaNew: parseFloat(campo(l, 'PRECOVENDA_NEW')),
    }))
    .filter(l =>
      !isNaN(l.custoOld) && !isNaN(l.custoNew) &&
      l.custoOld > 0 && l.custoNew > 0 &&
      Math.abs(l.custoNew - l.custoOld) > 0.01
    )
    .sort((a, b) => dataMs(b.data) - dataMs(a.data))
    .slice(0, 25)
  , [logAlteracoes])

  if (!rows.length) return (
    <p style={{ color: '#475569', fontSize: 12, margin: 0 }}>Nenhuma alteração de custo registrada.</p>
  )

  return (
    <div style={{ overflowX: 'auto', marginTop: 4 }}>
      <table style={S.table}>
        <thead>
          <tr>
            {['Data', 'Custo ant.', 'Custo novo', 'Var. custo', 'Venda ant.', 'Venda nova', 'Var. venda'].map(h => (
              <th key={h} style={S.th}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((l, i) => {
            const vC = ((l.custoNew - l.custoOld) / l.custoOld * 100)
            const vV = (l.vendaOld > 0) ? ((l.vendaNew - l.vendaOld) / l.vendaOld * 100) : null
            return (
              <tr key={i} style={{ background: i % 2 === 0 ? '#0f172a' : '#0d1526' }}>
                <td style={S.td}>
                  {l.data ? new Date(l.data).toLocaleDateString('pt-BR') : '—'}
                </td>
                <td style={{ ...S.td, color: '#64748b' }}>{brl(l.custoOld)}</td>
                <td style={{ ...S.td, fontWeight: 700, color: '#e2e8f0' }}>{brl(l.custoNew)}</td>
                <td style={S.td}><PctSpan v={vC} positiveGreen={false} /></td>
                <td style={{ ...S.td, color: '#64748b' }}>{brl(l.vendaOld)}</td>
                <td style={{ ...S.td, fontWeight: 700, color: '#e2e8f0' }}>{brl(l.vendaNew)}</td>
                <td style={S.td}><PctSpan v={vV} positiveGreen={true} /></td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ─── painel IA ────────────────────────────────────────────────────────────────

const PainelIA = ({ analise, logAlteracoes = [] }) => {
  if (!analise || analise.erro) return (
    <div style={S.erroBox}>
      <span style={{ fontSize: 28 }}>🤖</span>
      <p style={{ margin: 0, color: '#94a3b8', fontSize: 13 }}>
        {analise?.erro || 'Análise não disponível'}
      </p>
    </div>
  )

  const { resumo_geral, estoque, demanda, custos, alertas, insights, acoes_recomendadas } = analise
  const admin = isAdmin()

  const custoAtual  = custos?.custo_atual  ?? null
  const custoMedio  = custos?.custo_medio  ?? null
  const precoVenda  = custos?.preco_venda_atual ?? null
  const margem      = custos?.margem_atual_estimada_pct ?? null

  return (
    <div style={S.painel}>

      {resumo_geral && admin && <p style={S.resumo}>{resumo_geral}</p>}

      <div style={S.metricsRow}>
        <Metric valor={estoque?.saldo_atual}     label="Saldo atual" />
        <Metric valor={estoque?.total_entradas}  label="Entradas"    color="#4ade80" />
        <Metric valor={estoque?.total_saidas}    label="Saídas"      color="#f87171" />
        <Metric valor={estoque?.dias_cobertura}  label="Dias cobertos" />
        <Metric valor={estoque?.media_saida_dia} label="Saída / dia" />
      </div>

      {admin && (custoAtual || custoMedio || precoVenda) && (
        <div style={S.metricsRow}>
          {custoAtual != null && (
            <Metric valor={brl(custoAtual)} label="Último custo" color="#fbbf24" small />
          )}
          {custoMedio != null && (
            <Metric valor={brl(custoMedio)} label="Custo médio" color="#fb923c" small />
          )}
          {precoVenda != null && (
            <Metric valor={brl(precoVenda)} label="Preço venda" color="#34d399" small />
          )}
          {margem != null && (
            <Metric valor={`${margem}%`} label="Margem est." color="#a78bfa" small />
          )}
        </div>
      )}

      {estoque && (
        <Section icon="📦" title="Estoque">
          <div style={S.cardGrid}>
            <Row label="Tendência"><Badge label={estoque.tendencia || '—'} /></Row>
            <Row label="Giro"><Badge label={estoque.giro || '—'} /></Row>
            <Row label="Período">
              <span style={{ color: '#94a3b8', fontSize: 12 }}>
                {estoque.periodo_analisado
                  ? `${estoque.periodo_analisado.inicio} → ${estoque.periodo_analisado.fim}`
                  : '—'}
              </span>
            </Row>
          </div>
        </Section>
      )}

      {demanda && (
        <Section icon="📈" title="Demanda">
          <p style={S.text}>{demanda.analise}</p>
          <div style={S.cardGrid}>
            <Row label="Risco ruptura">
              <Badge label={demanda.risco_ruptura || '—'} color={RISCO_COR[demanda.risco_ruptura] || '#334155'} />
            </Row>
            {demanda.pico_periodo && <Row label="Pico">{demanda.pico_periodo}</Row>}
            {demanda.sazonalidade_detectada != null && (
              <Row label="Sazonalidade">
                {demanda.sazonalidade_detectada ? '✅ Detectada' : '❌ Não detectada'}
              </Row>
            )}
          </div>
          {demanda.sugestao_reposicao && (
            <div style={S.sugestao}>
              <span style={{ color: '#67e8f9', fontSize: 11, fontWeight: 600 }}>💡 Sugestão de reposição</span>
              <p style={{ margin: 0, color: '#e2e8f0', fontSize: 13, marginTop: 4 }}>{demanda.sugestao_reposicao}</p>
            </div>
          )}
        </Section>
      )}

      {admin && custos && (
        <Section
          icon="💰"
          title="Custos & Margem"
          right={
            custoAtual != null
              ? <span style={{ color: '#fbbf24', fontWeight: 700, fontSize: 14 }}>{brl(custoAtual)}</span>
              : null
          }
        >
          <div style={S.cardGrid}>
            {custoAtual != null && (
              <Row label="Último custo">
                <span style={{ color: '#fbbf24', fontWeight: 700 }}>{brl(custoAtual)}</span>
              </Row>
            )}
            {custoMedio != null && (
              <Row label="Custo médio histórico">
                <span style={{ color: '#fb923c', fontWeight: 700 }}>{brl(custoMedio)}</span>
              </Row>
            )}
            {precoVenda != null && (
              <Row label="Preço de venda atual">
                <span style={{ color: '#34d399', fontWeight: 700 }}>{brl(precoVenda)}</span>
              </Row>
            )}
            {margem != null && (
              <Row label="Margem estimada">
                <span style={{ color: '#a78bfa', fontWeight: 700, fontSize: 14 }}>{margem}%</span>
              </Row>
            )}
            <Row label="Tendência custo">
              <Badge label={custos.tendencia_custo || '—'} />
            </Row>
            <Row label="Tendência preço venda">
              <Badge label={custos.tendencia_preco_venda || '—'} />
            </Row>
            {custos.maior_alta_pct != null && (
              <Row label="Maior alta de custo"><PctSpan v={custos.maior_alta_pct} positiveGreen={false} /></Row>
            )}
            {custos.maior_queda_pct != null && (
              <Row label="Maior queda de custo"><PctSpan v={custos.maior_queda_pct} positiveGreen={true} /></Row>
            )}
            {custos.total_alteracoes != null && (
              <Row label="Total de alterações">
                <span style={{ color: '#e2e8f0' }}>{custos.total_alteracoes}x</span>
              </Row>
            )}
          </div>

          {custos.analise && <p style={{ ...S.text, marginTop: 4 }}>{custos.analise}</p>}

          <div>
            <p style={{ color: '#64748b', fontSize: 11, margin: '8px 0 4px', fontWeight: 600, letterSpacing: '0.05em' }}>
              📋 HISTÓRICO DE VARIAÇÕES
            </p>
            <TabelaLog logAlteracoes={logAlteracoes} />
          </div>
        </Section>
      )}

      {alertas?.length > 0 && (
        <Section icon="⚠️" title="Alertas">
          <div style={S.tagRow}>
            {alertas.map((a, i) => (
              <span key={i} style={{ ...S.tag, borderColor: '#f59e0b', color: '#fcd34d' }}>{a}</span>
            ))}
          </div>
        </Section>
      )}

      {insights?.length > 0 && (
        <Section icon="💡" title="Insights">
          {insights.map((ins, i) => (
            <div key={i} style={S.insightItem}>
              <span style={S.insightBullet}>›</span>
              <span>{ins}</span>
            </div>
          ))}
        </Section>
      )}

      {acoes_recomendadas?.length > 0 && (
        <Section icon="🎯" title="Ações Recomendadas">
          {acoes_recomendadas.map((ac, i) => (
            <div key={i} style={S.acaoItem}>
              <span style={S.acaoNum}>{i + 1}</span>
              <span>{ac}</span>
            </div>
          ))}
        </Section>
      )}
    </div>
  )
}

// ─── chip de resumo analítico ─────────────────────────────────────────────────

const Chip = ({ valor, label, color }) => (
  <div style={S.chip}>
    <span style={{ ...S.chipVal, ...(color ? { color } : {}) }}>{valor ?? '—'}</span>
    <span style={S.chipLbl}>{label}</span>
  </div>
)

// ─── estilos ──────────────────────────────────────────────────────────────────

const S = {
  painel: {
    display: 'flex', flexDirection: 'column', gap: 16, padding: 16,
    fontFamily: "'DM Mono','Fira Mono',monospace", fontSize: 13, color: '#94a3b8',
  },
  erroBox: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 12, padding: 40, color: '#94a3b8',
  },
  resumo: {
    margin: 0, color: '#cbd5e1', fontSize: 13, lineHeight: 1.7,
    padding: '12px 16px', background: '#1e293b', borderRadius: 8,
    borderLeft: '3px solid #818cf8',
  },
  metricsRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  metric: {
    flex: '1 1 70px', display: 'flex', flexDirection: 'column', alignItems: 'center',
    background: '#1e293b', borderRadius: 8, padding: '10px 12px',
  },
  metricVal: { color: '#e2e8f0', fontSize: 20, fontWeight: 700, lineHeight: 1.2 },
  metricLbl: {
    color: '#64748b', fontSize: 10, marginTop: 3,
    textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: 'center',
  },
  section: {
    background: '#1e293b', borderRadius: 10, padding: '12px 14px',
    display: 'flex', flexDirection: 'column', gap: 10,
  },
  sectionHead: { display: 'flex', alignItems: 'center', gap: 8 },
  sectionTitle: { color: '#e2e8f0', fontSize: 14, fontWeight: 700 },
  cardGrid: { display: 'flex', flexDirection: 'column', gap: 8 },
  kv: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '6px 10px', background: '#0f172a', borderRadius: 6,
  },
  kvLabel: { color: '#64748b', fontSize: 12 },
  kvValue: { color: '#cbd5e1', fontSize: 13 },
  badge: {
    fontSize: 11, fontWeight: 700, padding: '3px 10px',
    borderRadius: 99, color: '#f1f5f9', whiteSpace: 'nowrap',
  },
  text: { color: '#94a3b8', fontSize: 13, lineHeight: 1.6, margin: 0 },
  sugestao: {
    background: '#0f172a', borderRadius: 8,
    padding: '10px 12px', borderLeft: '3px solid #67e8f9',
  },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th: {
    color: '#64748b', fontSize: 11, fontWeight: 600, padding: '6px 10px',
    textAlign: 'left', borderBottom: '1px solid #334155', whiteSpace: 'nowrap',
  },
  td: { color: '#cbd5e1', padding: '6px 10px', whiteSpace: 'nowrap' },
  tagRow: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  tag: {
    fontSize: 12, padding: '4px 12px', borderRadius: 6,
    border: '1px solid', background: 'transparent',
  },
  insightItem: {
    display: 'flex', gap: 8, alignItems: 'flex-start', color: '#94a3b8',
    fontSize: 13, lineHeight: 1.6, padding: '6px 10px',
    background: '#0f172a', borderRadius: 6,
  },
  insightBullet: { color: '#818cf8', fontWeight: 700, flexShrink: 0, fontSize: 16, lineHeight: 1.4 },
  acaoItem: {
    display: 'flex', gap: 10, alignItems: 'flex-start', color: '#cbd5e1',
    fontSize: 13, lineHeight: 1.6, padding: '8px 10px',
    background: '#0f172a', borderRadius: 6, borderLeft: '2px solid #818cf8',
  },
  acaoNum: {
    background: '#818cf8', color: '#fff', borderRadius: 99,
    width: 20, height: 20, minWidth: 20,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 11, fontWeight: 700, flexShrink: 0,
  },

  // resumo analítico da listagem
  resumoBar: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 },
  chip: {
    flex: '1 1 100px', background: '#f8fafc', border: '1px solid #e2e8f0',
    borderRadius: 8, padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 2,
  },
  chipVal: { fontSize: 16, fontWeight: 700, color: '#1e293b', lineHeight: 1.1 },
  chipLbl: {
    fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em',
  },
}

// ─── modal principal ──────────────────────────────────────────────────────────

// Colunas da tabela — campo != null torna a coluna ordenável
const COLUNAS = [
  { label: 'Origem',         campo: null },
  { label: 'Cliente',        campo: null },
  { label: 'Valor',          campo: null },
  { label: 'Quantidade',     campo: 'QUANTIDADE' },
  { label: 'Saldo anterior', campo: 'SALDO_ESTOQUE' },
  { label: 'Tipo',           campo: null },
  { label: 'Data',           campo: 'DATA' },
]

const MovimentacaoEstoqueModal = ({
  produto,
  showModal,
  handleClose,
  historico,
  analise: analiseProp,
  logAlteracoes = [],
}) => {
  // Padrão: filtro em "Saída" e ordenação por data (mais recente primeiro)
  const [filtro, setFiltro] = useState('Saida')
  const [ordem, setOrdem] = useState({ campo: 'DATA', dir: 'desc' })
  const [paginaAtual, setPaginaAtual] = useState(1)
  const [drawerAberto, setDrawerAberto] = useState(false)
  const itensPorPagina = 50

  const listaHistorico = Array.isArray(historico)
    ? historico
    : Array.isArray(historico?.movimentacoes)
      ? historico.movimentacoes
      : []

  const analise = analiseProp ?? historico?.analise ?? null

  // 1) filtra por tipo
  const historicoFiltrado = useMemo(() => (
    filtro === 'Todos'
      ? listaHistorico
      : listaHistorico.filter(m => getColorClass(m.DESCRICAO) === filtro)
  ), [listaHistorico, filtro])

  // 2) ordena (evita a listagem "misturada")
  const historicoOrdenado = useMemo(() => {
    const c = ordem.campo
    const dir = ordem.dir === 'asc' ? 1 : -1
    return [...historicoFiltrado].sort((a, b) => {
      let va, vb
      if (c === 'DATA') { va = dataMs(a.DATA); vb = dataMs(b.DATA) }
      else if (c === 'QUANTIDADE' || c === 'SALDO_ESTOQUE') {
        va = Number(a[c]) || 0; vb = Number(b[c]) || 0
      } else { va = a[c]; vb = b[c] }
      if (va < vb) return -1 * dir
      if (va > vb) return  1 * dir
      return 0
    })
  }, [historicoFiltrado, ordem])

  // 3) resumo analítico da visão atual (respeita o filtro)
  const resumo = useMemo(() => {
    let qtdEntradas = 0, qtdSaidas = 0, nEntradas = 0, nSaidas = 0, nBalanco = 0, nCallback = 0
    let custoSaidas = 0
    listaHistorico.forEach(m => {
      const tipo = getColorClass(m.DESCRICAO)
      const q = Number(m.QUANTIDADE) || 0
      if (tipo === 'Entrada')  { nEntradas++;  qtdEntradas += q }
      else if (tipo === 'Saida') { nSaidas++;  qtdSaidas += q; custoSaidas += q * (Number(m.CUSTO) || 0) }
      else if (tipo === 'Balanço') nBalanco++
      else if (tipo === 'CallBack') nCallback++
    })
    // saldo atual = saldo da movimentação mais recente
    const maisRecente = listaHistorico.reduce(
      (acc, m) => (dataMs(m.DATA) >= dataMs(acc?.DATA) ? m : acc),
      listaHistorico[0]
    )
    const datas = listaHistorico.map(m => dataMs(m.DATA)).filter(Boolean)
    return {
      qtdEntradas, qtdSaidas, nEntradas, nSaidas, nBalanco, nCallback,
      custoSaidas,
      saldoAtual: maisRecente?.SALDO_ESTOQUE,
      periodoIni: datas.length ? new Date(Math.min(...datas)) : null,
      periodoFim: datas.length ? new Date(Math.max(...datas)) : null,
      total: listaHistorico.length,
    }
  }, [listaHistorico])

  const indiceInicial     = (paginaAtual - 1) * itensPorPagina
  const historicoPaginado = historicoOrdenado.slice(indiceInicial, indiceInicial + itensPorPagina)
  const totalPaginas      = Math.ceil(historicoOrdenado.length / itensPorPagina)

  const mudarPagina = (p) => { if (p > 0 && p <= totalPaginas) setPaginaAtual(p) }

  const alternarOrdem = (c) => {
    if (!c) return
    setOrdem(o => o.campo === c
      ? { campo: c, dir: o.dir === 'asc' ? 'desc' : 'asc' }
      : { campo: c, dir: 'desc' })
    setPaginaAtual(1)
  }

  const setaOrdem = (c) =>
    ordem.campo === c ? (ordem.dir === 'asc' ? ' ▲' : ' ▼') : ''

  const renderizarPaginas = () => {
    const paginas = []
    let start = Math.max(paginaAtual - 2, 1)
    let end   = Math.min(start + 4, totalPaginas)
    if (end - start < 4) start = Math.max(end - 4, 1)
    for (let i = start; i <= end; i++)
      paginas.push(
        <Pagination.Item key={i} active={paginaAtual === i} onClick={() => mudarPagina(i)}>
          {i}
        </Pagination.Item>
      )
    return paginas
  }

  const fmtData = (d) => (d ? d.toLocaleDateString('pt-BR') : '—')

  return (
    <Modal show={showModal} onHide={handleClose} dialogClassName="custom-modalzpl">
      <Modal.Header closeButton>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
          <Modal.Title style={{
            flex: 1, fontSize: 15, minWidth: 0,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            Histórico — {produto?.nome}
          </Modal.Title>
          <button
            onClick={() => setDrawerAberto(d => !d)}
            style={{
              background: drawerAberto ? '#818cf8' : 'transparent',
              border: '1.5px solid #818cf8', borderRadius: 8,
              color: drawerAberto ? '#fff' : '#818cf8',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7,
              padding: '5px 14px', fontSize: 13, fontWeight: 600,
              transition: 'all 0.18s', whiteSpace: 'nowrap', flexShrink: 0,
            }}
          >
            <IconIA size={15} />
            {drawerAberto ? 'Fechar' : 'Análise IA'}
          </button>
        </div>
      </Modal.Header>

      <Modal.Body style={{ padding: 0, display: 'flex', overflow: 'hidden', maxHeight: '82vh' }}>

        {/* ── drawer IA ── */}
        {drawerAberto && (
          <div style={{
            width: 500, minWidth: 500,
            borderRight: '1px solid #1e293b',
            overflowY: 'auto', background: '#0a0f1e',
          }}>
            <PainelIA analise={analise} logAlteracoes={logAlteracoes} />
          </div>
        )}

        {/* ── tabela ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16, minWidth: 0 }}>

          {/* resumo analítico */}
          <div style={S.resumoBar}>
            <Chip valor={resumo.saldoAtual != null ? num(resumo.saldoAtual) : '—'} label="Saldo atual" />
            <Chip valor={num(resumo.qtdSaidas)}  label={`Saídas (${resumo.nSaidas})`}   color="#dc2626" />
            <Chip valor={num(resumo.qtdEntradas)} label={`Entradas (${resumo.nEntradas})`} color="#16a34a" />
            <Chip valor={resumo.total} label="Total mov." />
            {isAdmin() && resumo.custoSaidas > 0 && (
              <Chip valor={brl(resumo.custoSaidas)} label="Custo saídas" color="#d97706" />
            )}
            <Chip valor={`${fmtData(resumo.periodoIni)} → ${fmtData(resumo.periodoFim)}`} label="Período" />
          </div>

          <Form.Group controlId="filtroTipo" className="mb-2">
            <Form.Label style={{ fontSize: 12 }}>Filtrar por Tipo:</Form.Label>
            <Form.Control
              as="select" value={filtro}
              onChange={(e) => { setFiltro(e.target.value); setPaginaAtual(1) }}
              style={{ fontSize: 12 }}
            >
              <option value="Saida">Saída</option>
              <option value="Entrada">Entrada</option>
              <option value="Balanço">Balanço</option>
              <option value="CallBack">CallBack</option>
              <option value="Todos">Todos</option>
            </Form.Control>
          </Form.Group>

          {historicoPaginado.length > 0 ? (
            <Table striped bordered hover>
              <thead>
                <tr>
                  {COLUNAS.map(col => (
                    <th
                      key={col.label}
                      className="text-secondary"
                      onClick={() => alternarOrdem(col.campo)}
                      style={{
                        fontSize: 12,
                        cursor: col.campo ? 'pointer' : 'default',
                        userSelect: 'none',
                        whiteSpace: 'nowrap',
                        color: ordem.campo === col.campo ? '#4f46e5' : undefined,
                      }}
                      title={col.campo ? 'Clique para ordenar' : undefined}
                    >
                      {col.label}{setaOrdem(col.campo)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {historicoPaginado.map((m, i) => (
                  <tr key={i}>
                    <td style={{ fontSize: 12 }}>{m.ORIGEM}</td>
                    <td style={{ fontSize: 12 }}>{m.CLIENTE}</td>
                    <td style={{ fontSize: 12 }}>
                      {m.ORIGEM === 'Callback Estoque'
                        ? 'movimentação de estoque bling'
                        : isAdmin()
                          ? m.CUSTO != null ? `R$ ${Number(m.CUSTO).toFixed(2)} (Custo)` : '-'
                          : ''}
                    </td>
                    <td style={{ fontSize: 12 }} className={getColor(m.DESCRICAO)}>
                      {m.QUANTIDADE != null
                        ? (getColorClass(m.DESCRICAO) === 'Saida' ? '-' : '') + Number(m.QUANTIDADE).toFixed(2)
                        : '0.00'}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {m.SALDO_ESTOQUE != null ? Number(m.SALDO_ESTOQUE).toFixed(2) : 'Não há dados disponíveis'}
                    </td>
                    <td style={{ fontSize: 12 }} className={getColor(m.DESCRICAO)}>
                      {getColorClass(m.DESCRICAO)}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {m.DATA ? new Date(m.DATA).toLocaleDateString('pt-BR') : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p>Nenhuma movimentação registrada para este filtro.</p>
          )}

          {totalPaginas > 1 && (
            <Pagination>
              <Pagination.First onClick={() => mudarPagina(1)}              disabled={paginaAtual === 1} />
              <Pagination.Prev  onClick={() => mudarPagina(paginaAtual - 1)} disabled={paginaAtual === 1} />
              {renderizarPaginas()}
              <Pagination.Next  onClick={() => mudarPagina(paginaAtual + 1)} disabled={paginaAtual === totalPaginas} />
              <Pagination.Last  onClick={() => mudarPagina(totalPaginas)}    disabled={paginaAtual === totalPaginas} />
            </Pagination>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>Fechar</Button>
      </Modal.Footer>
    </Modal>
  )
}

export default MovimentacaoEstoqueModal